import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { laseaAuthService } from '../../services/laseaAuthService';
import ThemeToggle from '../../components/ui/ThemeToggle';


export default function ClientLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showSecurityQuestion, setShowSecurityQuestion] = useState(false);
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');

  const handleClientLogin = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { role } = await laseaAuthService?.signIn(email, password);

      if (role !== 'customer') {
        await laseaAuthService?.signOut();
        throw new Error('Access denied. Customer credentials required.');
      }

      navigate('/client-dashboard');
    } catch (err) {
      setError(err?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      await laseaAuthService?.resetPassword(email);
      setResetEmailSent(true);
      // Note: Due to Supabase email configuration, password reset emails may not be sent.
      // Please check with your administrator if you don't receive the email.
    } catch (err) {
      setError(err?.message || 'Failed to send reset email. Please verify your email address and try again. If the issue persists, contact support at MGDockery@me.com');
    } finally {
      setLoading(false);
    }
  };

  const handleShowSecurityQuestion = async () => {
    setError('');
    setLoading(true);
    try {
      const question = await laseaAuthService?.getSecurityQuestion(email);
      setSecurityQuestion(question);
      setShowForgotPassword(false);
      setShowSecurityQuestion(true);
    } catch (err) {
      setError(err?.message || 'Could not retrieve security question');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySecurityAnswer = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);
    try {
      await laseaAuthService?.verifySecurityAnswer(email, securityAnswer);
      // If answer is correct, show a message with instructions
      setError('');
      setShowSecurityQuestion(false);
      // Show recovery options
      alert('To recover your access:\n\n1. Contact LaSea Support\n2. Email: MGDockery@me.com\n3. Tell them your registered email and they will help you reset your password');
      // Reset the form
      setEmail('');
      setPassword('');
      setSecurityAnswer('');
    } catch (err) {
      setError(err?.message || 'Incorrect security answer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center min-h-screen bg-gray-900 p-6">
      {/* Theme Toggle in Top Corner - Hidden since theme is locked */}
      <div className="absolute top-6 right-6 z-10 hidden">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-md bg-gray-800 rounded-lg shadow-md p-8">
        <h2 className="text-2xl font-bold text-center mb-6 text-white">
          {showForgotPassword ? 'Reset Password' : 'Client Login'}
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-900 border border-red-700 text-red-200 rounded">
            {error}
          </div>
        )}

        {resetEmailSent && (
          <div className="mb-4 p-3 bg-green-900 border border-green-700 text-green-200 rounded">
            Password reset email sent! Check your inbox for instructions.
          </div>
        )}

        {!showForgotPassword ? (
          <form onSubmit={handleClientLogin}>
            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={loading}
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e?.target?.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={loading}
              />
            </div>

            <div className="mb-6 flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-blue-400 hover:underline"
              >
                Forgot Password?
              </button>
              <button
                type="button"
                onClick={handleShowSecurityQuestion}
                disabled={!email || loading}
                className="text-blue-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Forgot Security Answer?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        ) : showSecurityQuestion ? (
          <form onSubmit={handleVerifySecurityAnswer}>
            <p className="text-sm text-gray-400 mb-4">
              Answer your security question to recover access to your account.
            </p>

            <div className="mb-6">
              <label className="block text-gray-300 mb-2">Your Security Question:</label>
              <p className="text-sm text-gray-200 bg-gray-700 p-3 rounded-lg mb-4">
                {securityQuestion}
              </p>

              <label className="block text-gray-300 mb-2">Your Answer</label>
              <input
                type="text"
                value={securityAnswer}
                onChange={(e) => setSecurityAnswer(e?.target?.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 mb-3"
            >
              {loading ? 'Verifying...' : 'Verify Answer'}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowSecurityQuestion(false);
                setSecurityAnswer('');
                setError('');
              }}
              className="w-full text-gray-400 hover:text-gray-200"
            >
              Back to Login
            </button>
          </form>
        ) : (
          <form onSubmit={handleForgotPassword}>
            <p className="text-sm text-gray-400 mb-4">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            <div className="mb-6">
              <label className="block text-gray-300 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 mb-3"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(false);
                setResetEmailSent(false);
                setError('');
              }}
              className="w-full text-gray-400 hover:text-gray-200"
            >
              Back to Login
            </button>
          </form>
        )}

        <div className="mt-4 text-center">
          <button
            onClick={() => navigate('/client-registration')}
            className="text-blue-400 hover:underline"
          >
            Need an account? Register here
          </button>
        </div>
      </div>
    </div>
  );
}